import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { timeAgo } from '../lib/timeAgo';
import { PageHeader } from '../ui/PageHeader';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { StatCard } from '../ui/StatCard';
import { SkeletonRows } from '../ui/Skeleton';

/**
 * VENUE DESCRIPTIONS — one screen, two ways a venue page gets its description text.
 *
 * 1. REVIEW FEED. AI search writes a one-line description of a venue while answering a query.
 *    Those land in a review queue; approving one publishes it onto the venue.
 * 2. BY HAND. Search any venue and write/replace its description directly — the path that does not
 *    require the venue to be logged into the venue portal.
 *
 * ── WHICH COLUMN (verified against production 2026-08-18, read-only) ────────────────────────────
 * `venues` has exactly ONE description column: **`venues.google_description`** (plus its TTL
 * sibling `google_description_cached_at`). That is the column the app renders — see
 * `screens/venue-discovery.tsx:2224` (`setResolvedEditorialSummary`) → `:3197` `editorialText`.
 *
 *   const venueDescriptionText = (venueOwnProfile?.description ?? '').trim() || editorialText;
 *
 * So the precedence on the device is: the VENUE'S OWN portal profile text wins, and this column is
 * what shows when the venue has not written one — which is nearly every venue. The portal's text
 * lives on `venue_profile_submissions.description`, a DIFFERENT table with its own approval flow;
 * we deliberately never write there from here, because that is the venue's own words.
 *
 * ⚠️ There is a THIRD name floating around that does not exist: `VenueReviewScreen` has a
 * description editor gated on `venueFieldSupport.description`, which probes for a plain
 * `venues.description` column. That column is not in production, so that editor never renders.
 * Do not resurrect it — `google_description` is the one.
 *
 * ── UNAPPLIED DEPENDENCIES ──────────────────────────────────────────────────────────────────────
 * Every RPC below may be missing when this ships. The page degrades the same way `CityOverviews`
 * does: a 42883 / "could not find the function" is caught and shown as a set-up note, never as a
 * red error, because "the migration has not run yet" is not a failure.
 */

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * RPC plumbing
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Same test as `CityOverviews.rpcHomeCityCountsMissing` — PostgREST reports a function that is not
 * in the schema cache the same way whether the migration never ran or the signature differs, so
 * both land here and both mean "not wired up yet" from the operator's point of view.
 */
function rpcMissing(message: string, fn: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes(fn.toLowerCase()) ||
    m.includes('could not find the function') ||
    m.includes('in the schema cache') ||
    m.includes('42883')
  );
}

type RpcAttempt = { ok: boolean; data: unknown; usedVariant: number; missing: boolean; error: string | null };

/**
 * Call an RPC, trying each argument shape in turn.
 *
 * The queue RPCs are being written by a parallel session, so their exact parameter names are not
 * knowable from here. Rather than guess once and be wrong, try the plausible shapes: a signature
 * mismatch is indistinguishable from a missing function over PostgREST, so a wrong first guess
 * would otherwise render the whole feed as "not set up" forever. A REAL error (forbidden, a
 * constraint, a network fault) is returned immediately and never retried.
 */
async function rpcTry(fn: string, variants: Record<string, unknown>[]): Promise<RpcAttempt> {
  let lastMessage: string | null = null;
  for (let i = 0; i < variants.length; i += 1) {
    // The ladder is sequential by definition: each attempt only runs if the last one 404'd.
    const { data, error } = await supabase.rpc(fn, variants[i]);
    if (!error) return { ok: true, data, usedVariant: i, missing: false, error: null };
    lastMessage = error.message;
    if (!rpcMissing(error.message, fn)) {
      return { ok: false, data: null, usedVariant: i, missing: false, error: error.message };
    }
  }
  return { ok: false, data: null, usedVariant: -1, missing: true, error: lastMessage };
}

const SETUP_NOTE =
  'The generated-description queue is not in the database yet. Once the search-concierge migration ' +
  'is applied (admin_list_generated_descriptions / admin_approve_generated_description / ' +
  'admin_reject_generated_description), reload this page and the feed fills itself.';

const SET_DESCRIPTION_SETUP_NOTE =
  'Writing a description by hand needs admin_set_venue_description, which is not in the database ' +
  'yet. Apply supabase/migrations/20260864100000_admin_set_venue_description.sql, then reload.';

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * Row normalisation
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

type GeneratedRow = {
  id: string;
  venueId: string | null;
  venueName: string;
  cityName: string | null;
  description: string;
  query: string | null;
  createdAt: string | null;
  /** What the venue shows TODAY — filled in locally so "approve" never silently overwrites. */
  currentDescription: string | null;
};

function firstString(rec: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return null;
}

/**
 * The queue RPC may return a `TABLE(...)` (rows) or a single `jsonb` array — both arrive as an
 * array through supabase-js — or a wrapper object. Field names are normalised through aliases for
 * the same reason `rpcTry` exists: the writer of the migration is a parallel session.
 */
function normaliseRows(payload: unknown): GeneratedRow[] {
  let list: unknown = payload;
  if (list && !Array.isArray(list) && typeof list === 'object') {
    const rec = list as Record<string, unknown>;
    list = rec.items ?? rec.rows ?? rec.descriptions ?? rec.data ?? [];
  }
  if (!Array.isArray(list)) return [];

  const out: GeneratedRow[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const rec = raw as Record<string, unknown>;
    const id = firstString(rec, ['id', 'queue_id', 'description_id', 'generated_description_id']);
    const description = firstString(rec, [
      'description',
      'generated_description',
      'suggested_description',
      'text',
      'line',
      'body',
    ]);
    if (!id || !description) continue;
    out.push({
      id,
      venueId: firstString(rec, ['venue_id', 'venueId']),
      venueName:
        firstString(rec, ['venue_name', 'venue_name_user_input', 'name', 'venueName']) ?? 'Unknown venue',
      cityName: firstString(rec, ['city_name', 'city', 'cityName']),
      description,
      query: firstString(rec, ['query', 'search_query', 'user_query', 'prompt']),
      createdAt: firstString(rec, ['created_at', 'generated_at', 'submitted_at', 'inserted_at']),
      currentDescription: firstString(rec, ['current_description', 'existing_description', 'google_description']),
    });
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * Venue search (hand-written tab) — same RPC + same dropdown anatomy as VenueOutreachScreen
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

type VenueHit = {
  venue_id: string;
  venue_name: string | null;
  city_name: string | null;
  address: string | null;
  has_access: boolean;
};

const DESCRIPTION_MAX = 1200;

function VenueDescriptionWriter({ onSaved }: { onSaved: (venueId: string) => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<VenueHit[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<VenueHit | null>(null);
  const [draft, setDraft] = useState('');
  const [baseline, setBaseline] = useState('');
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const { showSuccess, showError, toastNode } = useToast();

  /**
   * ⚠️ SEQUENCED. This fires on every keystroke; without the guard a slow early request ("br") can
   * resolve after a later one and repopulate the dropdown for text you are no longer typing. Lifted
   * verbatim from `VenueOutreachScreen.AccessGranter` so both searches behave identically.
   */
  const searchSeq = useRef(0);
  const search = useCallback(async (next: string) => {
    setQ(next);
    setPicked(null);
    setSearchError(null);
    const seq = searchSeq.current + 1;
    searchSeq.current = seq;
    if (next.trim().length < 2) {
      setHits([]);
      return;
    }
    const { data, error } = await supabase.rpc('admin_search_venues_for_portal', {
      p_q: next.trim(),
      p_limit: 8,
    });
    if (searchSeq.current !== seq) return;
    if (error) {
      setHits([]);
      setSearchError(error.message);
      return;
    }
    setHits((data as VenueHit[]) ?? []);
  }, []);

  const pick = useCallback(
    async (hit: VenueHit) => {
      setPicked(hit);
      setHits([]);
      setDraft('');
      setBaseline('');
      setLoadingCurrent(true);
      // Plain SELECT: `venues_select_authenticated` already lets any signed-in account read a
      // non-hidden venue, so this needs no RPC and stays live even before the migration lands.
      const { data, error } = await supabase
        .from('venues')
        .select('google_description')
        .eq('id', hit.venue_id)
        .maybeSingle();
      setLoadingCurrent(false);
      if (error) {
        showError(`Could not read the current description: ${error.message}`);
        return;
      }
      const current = ((data as { google_description?: string | null } | null)?.google_description ?? '').trim();
      setDraft(current);
      setBaseline(current);
    },
    [showError],
  );

  const save = useCallback(
    async (nextText: string) => {
      if (!picked) return;
      setBusy(true);
      setSetupNote(null);
      try {
        const res = await rpcTry('admin_set_venue_description', [
          { p_venue_id: picked.venue_id, p_description: nextText.trim() || null },
        ]);
        if (res.missing) {
          setSetupNote(SET_DESCRIPTION_SETUP_NOTE);
          return;
        }
        if (!res.ok) throw new Error(res.error ?? 'Save failed');
        setDraft(nextText);
        setBaseline(nextText);
        showSuccess(nextText.trim() ? 'Description saved to the venue' : 'Description cleared');
        onSaved(picked.venue_id);
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setBusy(false);
      }
    },
    [picked, onSaved, showError, showSuccess],
  );

  const dirty = draft !== baseline;
  const tooLong = draft.trim().length > DESCRIPTION_MAX;

  return (
    <div className="space-y-4">
      {toastNode}
      {setupNote ? <EmptyState title="Saving is not set up yet" hint={setupNote} /> : null}

      <div className="admin-card space-y-3">
        <div className="admin-label-primary">Find a venue</div>
        <p className="text-[11px] leading-relaxed text-mutedDim">
          Any venue in the search corpus — the venue does not need a portal login, and nobody at the
          venue is notified. What you save here is what the venue page shows, unless the venue has
          written its own description in the portal, which always wins.
        </p>
        <div className="relative max-w-md">
          <input
            value={picked ? (picked.venue_name ?? '') : q}
            onChange={(e) => void search(e.target.value)}
            placeholder="Search a venue…"
            className="admin-input w-full text-xs"
            aria-label="Search a venue"
          />
          {searchError ? (
            <p className="mt-1 text-[11px] text-red-400">Search failed: {searchError}</p>
          ) : null}
          {!searchError && !picked && q.trim().length >= 2 && hits.length === 0 ? (
            <p className="mt-1 text-[11px] text-mutedDim">No venues match “{q.trim()}”.</p>
          ) : null}
          {hits.length > 0 && !picked ? (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-border bg-surfaceElevated shadow-lg">
              {hits.map((h) => (
                <button
                  key={h.venue_id}
                  type="button"
                  onClick={() => void pick(h)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-white/[0.05]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[12px] text-white">{h.venue_name}</span>
                    <span className="block truncate text-[10px] text-mutedDim">
                      {[h.city_name, h.address].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {picked ? (
        <div className="admin-card space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="min-w-0">
              <div className="admin-label-primary truncate">{picked.venue_name}</div>
              <div className="truncate text-[11px] text-mutedDim">
                {[picked.city_name, picked.address].filter(Boolean).join(' · ') || 'No address on file'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setPicked(null);
                setQ('');
                setDraft('');
                setBaseline('');
              }}
              className="admin-btn admin-btn-ghost admin-btn-sm"
            >
              Change venue
            </button>
          </div>

          {loadingCurrent ? (
            <SkeletonRows rows={3} />
          ) : (
            <>
              <label htmlFor="venue-description-draft" className="admin-label">
                Description
              </label>
              <textarea
                id="venue-description-draft"
                rows={5}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Two or three sentences, in the app's voice — what the room is like and who it is for."
                className="admin-input !h-auto !py-2.5 resize-y leading-relaxed"
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`text-[11px] ${tooLong ? 'text-red-400' : 'text-mutedDim'}`}>
                  {draft.trim().length} / {DESCRIPTION_MAX} characters
                  {baseline ? '' : ' · this venue has no description yet'}
                </span>
                <span className="flex items-center gap-2">
                  {baseline ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void save('')}
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      title="Remove the description from this venue page"
                    >
                      Clear
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={busy || !dirty || tooLong}
                    onClick={() => void save(draft)}
                    className="admin-btn admin-btn-primary admin-btn-sm"
                  >
                    {busy ? 'Saving…' : 'Save description'}
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * The queue card
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

function QueueCard({
  row,
  draft,
  busy,
  onDraftChange,
  onApprove,
  onReject,
}: {
  row: GeneratedRow;
  draft: string;
  busy: boolean;
  onDraftChange: (id: string, next: string) => void;
  onApprove: (row: GeneratedRow, text: string) => void;
  onReject: (row: GeneratedRow) => void;
}) {
  const edited = draft.trim() !== row.description.trim();
  const tooLong = draft.trim().length > DESCRIPTION_MAX;
  const willOverwrite = !!row.currentDescription && row.currentDescription.trim().length > 0;

  return (
    <div className="admin-card admin-card-hover space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <div className="admin-label-primary truncate">{row.venueName}</div>
          <div className="truncate text-[11px] text-mutedDim">{row.cityName ?? 'City unknown'}</div>
        </div>
        <div className="shrink-0 text-[11px] text-mutedDim">
          {row.createdAt ? timeAgo(row.createdAt) : 'Date unknown'}
        </div>
      </div>

      {row.query ? (
        <div className="text-[11px] leading-relaxed text-muted">
          <span className="admin-eyebrow mr-1.5">From search</span>“{row.query}”
        </div>
      ) : null}

      <textarea
        rows={3}
        value={draft}
        onChange={(e) => onDraftChange(row.id, e.target.value)}
        aria-label={`Description for ${row.venueName}`}
        className="admin-input !h-auto !py-2.5 resize-y leading-relaxed"
      />

      {willOverwrite ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2">
          <div className="admin-eyebrow mb-1 text-amber-200/80">Approving replaces what is live now</div>
          <p className="text-[11px] leading-relaxed text-muted">{row.currentDescription}</p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`text-[11px] ${tooLong ? 'text-red-400' : 'text-mutedDim'}`}>
          {draft.trim().length} / {DESCRIPTION_MAX} characters{edited ? ' · edited' : ''}
        </span>
        <span className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(row)}
            className="admin-btn admin-btn-ghost admin-btn-sm"
          >
            Reject
          </button>
          <button
            type="button"
            disabled={busy || tooLong || draft.trim().length === 0}
            onClick={() => onApprove(row, draft)}
            className="admin-btn admin-btn-primary admin-btn-sm"
          >
            {busy ? 'Working…' : 'Approve'}
          </button>
        </span>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * Screen
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

type Tab = 'queue' | 'write';

export function VenueDescriptionsScreen() {
  const [tab, setTab] = useState<Tab>('queue');
  const [rows, setRows] = useState<GeneratedRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  /**
   * ⚠️ SEPARATE FROM `setupNote` ON PURPOSE. `setupNote` means the LIST rpc is missing, so there is
   * nothing to draw and the empty state takes over the tab. An approve/reject that turns out to be
   * missing happens with a full queue on screen — writing that into `setupNote` would make the
   * operator's whole loaded queue vanish behind a set-up card. This one is a banner above the list.
   */
  const [actionNote, setActionNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { showSuccess, showError, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSetupNote(null);
    setActionNote(null);
    try {
      // No-args first: a function with a defaulted p_limit still resolves without it, so this is
      // the shape most likely to hit whatever signature the migration ends up declaring.
      const res = await rpcTry('admin_list_generated_descriptions', [{}, { p_limit: 200 }]);
      if (res.missing) {
        setRows([]);
        setSetupNote(SETUP_NOTE);
        return;
      }
      if (!res.ok) throw new Error(res.error ?? 'Could not load the queue');

      const parsed = normaliseRows(res.data);

      /**
       * Fill in what each venue shows TODAY. The queue RPC may or may not carry it, and approving
       * blind — over a description an operator already wrote by hand — is the one destructive thing
       * this screen can do. One keyed read over at most a couple of hundred ids.
       */
      const missingCurrent = Array.from(
        new Set(parsed.filter((r) => r.venueId && r.currentDescription == null).map((r) => r.venueId as string)),
      );
      if (missingCurrent.length > 0) {
        const { data: venueRows, error: venueErr } = await supabase
          .from('venues')
          .select('id, google_description')
          .in('id', missingCurrent);
        if (!venueErr && venueRows) {
          const byId = new Map<string, string | null>();
          for (const v of venueRows as { id: string; google_description: string | null }[]) {
            byId.set(v.id, v.google_description);
          }
          for (const r of parsed) {
            if (r.venueId && r.currentDescription == null) {
              r.currentDescription = byId.get(r.venueId) ?? null;
            }
          }
        }
      }

      setRows(parsed);
      setDrafts((prev) => {
        // Keep any in-progress edit for a row that is still pending; drop the rest.
        const next: Record<string, string> = {};
        for (const r of parsed) if (prev[r.id] != null) next[r.id] = prev[r.id];
        return next;
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load the queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onDraftChange = useCallback((id: string, next: string) => {
    setDrafts((prev) => ({ ...prev, [id]: next }));
  }, []);

  const approve = useCallback(
    async (row: GeneratedRow, text: string) => {
      const clean = text.trim();
      if (!clean) return;
      setBusyId(row.id);
      try {
        const res = await rpcTry('admin_approve_generated_description', [
          { p_id: row.id, p_description: clean },
          { p_id: row.id },
          { p_queue_id: row.id, p_description: clean },
          { p_queue_id: row.id },
        ]);
        if (res.missing) {
          setActionNote(SETUP_NOTE);
          showError('Approving is not set up yet — see the note above.');
          return;
        }
        if (!res.ok) throw new Error(res.error ?? 'Approve failed');

        /**
         * ⚠️ THE EDIT MUST NOT BE SILENTLY DROPPED. Variants 1 and 3 hand the edited text to the
         * approve RPC. If only the no-text signature exists, that RPC published the ORIGINAL
         * generated line — so when the operator changed it, write the edit over the top through
         * `admin_set_venue_description`. Anything else would show an approved card whose text is
         * not what the operator read and accepted.
         */
        const approvedWithText = res.usedVariant === 0 || res.usedVariant === 2;
        const editedAway = clean !== row.description.trim();
        if (!approvedWithText && editedAway && row.venueId) {
          const patch = await rpcTry('admin_set_venue_description', [
            { p_venue_id: row.venueId, p_description: clean },
          ]);
          if (!patch.ok) {
            showError(
              `Approved, but your edited wording could not be saved (${
                patch.missing ? 'admin_set_venue_description is not applied yet' : patch.error ?? 'unknown error'
              }). The venue is showing the original generated text.`,
            );
            setRows((prev) => prev.filter((r) => r.id !== row.id));
            return;
          }
        }

        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        showSuccess(`Description published to ${row.venueName}`);
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Approve failed');
      } finally {
        setBusyId(null);
      }
    },
    [showError, showSuccess],
  );

  const reject = useCallback(
    async (row: GeneratedRow) => {
      setBusyId(row.id);
      try {
        const res = await rpcTry('admin_reject_generated_description', [
          { p_id: row.id },
          { p_queue_id: row.id },
        ]);
        if (res.missing) {
          setActionNote(SETUP_NOTE);
          showError('Rejecting is not set up yet — see the note above.');
          return;
        }
        if (!res.ok) throw new Error(res.error ?? 'Reject failed');
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        showSuccess('Rejected — nothing was written to the venue');
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Reject failed');
      } finally {
        setBusyId(null);
      }
    },
    [showError, showSuccess],
  );

  const stats = useMemo(() => {
    const venues = new Set(rows.map((r) => r.venueId ?? r.id));
    const overwrites = rows.filter((r) => (r.currentDescription ?? '').trim().length > 0).length;
    const dated = rows.map((r) => r.createdAt).filter((d): d is string => !!d).sort();
    return {
      pending: rows.length,
      venues: venues.size,
      overwrites,
      oldest: dated.length > 0 ? timeAgo(dated[0]) : null,
    };
  }, [rows]);

  return (
    <div className="admin-page max-w-5xl mx-auto">
      {toastNode}
      <PageHeader
        title="Descriptions"
        subtitle="Approve the descriptions AI search writes for a venue, or write one by hand. Both publish to the venue page — the venue's own portal profile, if it has one, still wins over this text."
        actions={
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="admin-btn admin-btn-secondary admin-btn-sm"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      <div className="mb-6 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <StatCard label="Pending review" value={setupNote ? null : stats.pending} />
        <StatCard label="Venues waiting" value={setupNote ? null : stats.venues} />
        <StatCard
          label="Would replace text"
          value={setupNote ? null : stats.overwrites}
          sub="Already showing a description"
        />
        <StatCard label="Oldest in queue" value={setupNote ? null : stats.oldest} />
      </div>

      <div className="mb-5 flex items-center gap-2">
        <button
          type="button"
          className="admin-pill"
          data-active={tab === 'queue'}
          onClick={() => setTab('queue')}
        >
          Generated queue{!setupNote && stats.pending > 0 ? ` (${stats.pending})` : ''}
        </button>
        <button
          type="button"
          className="admin-pill"
          data-active={tab === 'write'}
          onClick={() => setTab('write')}
        >
          Write by hand
        </button>
      </div>

      {tab === 'queue' ? (
        <div className="space-y-3">
          {error ? <ErrorBanner>{error}</ErrorBanner> : null}
          {actionNote ? (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-[12px] leading-relaxed text-amber-100/90">
              {actionNote}
            </div>
          ) : null}
          {setupNote ? (
            <EmptyState
              title="The generated-description queue is not set up yet"
              hint={setupNote}
              action={
                <button type="button" onClick={() => void load()} className="admin-btn admin-btn-secondary admin-btn-sm">
                  Check again
                </button>
              }
            />
          ) : loading ? (
            <SkeletonRows rows={5} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing waiting"
              hint="A card lands here whenever AI search writes a description for a venue. Approve it to put that text on the venue page."
            />
          ) : (
            rows.map((r) => (
              <QueueCard
                key={r.id}
                row={r}
                draft={drafts[r.id] ?? r.description}
                busy={busyId === r.id}
                onDraftChange={onDraftChange}
                onApprove={(row, text) => void approve(row, text)}
                onReject={(row) => void reject(row)}
              />
            ))
          )}
        </div>
      ) : (
        <VenueDescriptionWriter onSaved={() => void load()} />
      )}
    </div>
  );
}
